import React, { useState, useEffect, useRef } from 'react';
import { Image as ImageIcon, Upload, Trash2, RefreshCw, History, RotateCcw, Save } from 'lucide-react';
import { brandingAPI } from '../services/api';
import { authedMediaUrl } from '../utils/media';
import { formatClinicDateTime } from '../utils/clinicTime';
import { useToast, useConfirm } from './UIProvider';
import ClinicLogo, { refreshBranding } from './ClinicLogo';
import TablePager, { usePagedRows } from './TablePager';

/**
 * Settings → Branding. Everything that identifies the hospital on something a patient sees.
 *
 * <p>Clinic Details is the name, address, phone and timings printed on the prescription letterhead
 * and the receipt. These were literals compiled into the frontend and into ReceiptService until
 * now, which is how a prescription came to be printed with "Main Road, City Name" while a receipt
 * from the same system claimed "Lahore, Pakistan".
 *
 * <p>Clinic Logo replaces the built-in one everywhere: app header and sidebar, login screen,
 * Display Board, and printed prescriptions. Removing it reverts to the built-in logo. Every upload
 * is kept in an append-only history so any previous design can be re-activated.
 *
 * <p>Gated by the SETTINGS_BRANDING permission.
 */
const EMPTY_PROFILE = { nameEnglish: '', nameUrdu: '', address: '', phone: '', timings: '' };

// Rendered in the order they appear on the letterhead, so the form reads like the printout.
const PROFILE_FIELDS = [
  { key: 'nameEnglish', label: 'Hospital Name', required: true, placeholder: 'Perveen Aslam Memorial Hospital' },
  { key: 'nameUrdu', label: 'Hospital Name (Urdu)', urdu: true, placeholder: 'پروین اسلم میموریل ہسپتال' },
  { key: 'address', label: 'Address', wide: true, placeholder: 'Street, area, city' },
  { key: 'phone', label: 'Phone', placeholder: '042-35000000' },
  { key: 'timings', label: 'Timings', placeholder: 'Mon-Sat: 9:00 AM - 9:00 PM' },
];

const BrandingSettings = ({ hasPermission }) => {
  const notify = useToast();
  const confirm = useConfirm();
  const can = (action) =>
    typeof hasPermission === 'function' ? hasPermission('SETTINGS_BRANDING', action) : true;

  const [branding, setBranding] = useState(null);
  const [history, setHistory] = useState(null);
  // Clinic Details form. `saved` is the last persisted copy, so the Save button can tell an actual
  // edit from a re-render and the admin can discard back to it.
  const [profile, setProfile] = useState(EMPTY_PROFILE);
  const [savedProfile, setSavedProfile] = useState(EMPTY_PROFILE);
  const [savingProfile, setSavingProfile] = useState(false);
  const [working, setWorking] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const info = await brandingAPI.get();
      setBranding(info);
      const p = { ...EMPTY_PROFILE, ...(info.profile || {}) };
      setProfile(p);
      setSavedProfile(p);
    } catch (e) {
      setBranding({ hasCustomLogo: false });
    }
    try {
      const rows = await brandingAPI.getHistory();
      setHistory(Array.isArray(rows) ? rows : []);
    } catch (e) {
      setHistory([]);
    }
  };
  useEffect(() => { load(); }, []);

  const profileDirty = PROFILE_FIELDS.some((f) => (profile[f.key] || '') !== (savedProfile[f.key] || ''));

  const handleSaveProfile = async () => {
    if (!profile.nameEnglish.trim()) {
      notify('error', 'The hospital name is required');
      return;
    }
    setSavingProfile(true);
    try {
      const saved = await brandingAPI.updateProfile(profile);
      const p = { ...EMPTY_PROFILE, ...saved };
      setProfile(p);
      setSavedProfile(p);
      notify('success', 'Clinic details updated. New printouts will use them');
    } catch (err) {
      notify('error', err.message || 'Failed to save the clinic details');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      notify('error', `"${file.name}" exceeds the 2MB limit`);
      return;
    }
    setWorking(true);
    try {
      await brandingAPI.uploadLogo(file);
      await load();
      refreshBranding(); // every mounted logo in the app swaps immediately
      notify('success', 'Logo updated across the app');
    } catch (err) {
      notify('error', err.message || 'Failed to upload logo');
    } finally {
      setWorking(false);
    }
  };

  const handleRemove = async () => {
    const ok = await confirm({
      title: 'Remove custom logo?',
      message:
        'The app reverts to the built-in PAMH logo everywhere. Your uploads stay in Logo History below, so you can bring any of them back later.',
      confirmText: 'Remove',
      tone: 'danger',
    });
    if (!ok) return;
    setWorking(true);
    try {
      await brandingAPI.removeLogo();
      await load();
      refreshBranding();
      notify('success', 'Reverted to the built-in logo');
    } catch (err) {
      notify('error', err.message || 'Failed to remove logo');
    } finally {
      setWorking(false);
    }
  };

  const handleActivate = async (row) => {
    const ok = await confirm({
      title: 'Use this logo?',
      message: 'This version replaces the current logo everywhere in the app.',
      confirmText: 'Use This Logo',
    });
    if (!ok) return;
    setWorking(true);
    try {
      await brandingAPI.activateVersion(row.id);
      await load();
      refreshBranding();
      notify('success', 'Logo updated across the app');
    } catch (err) {
      notify('error', err.message || 'Failed to switch logo');
    } finally {
      setWorking(false);
    }
  };

  const canEdit = can('EDIT');
  const historyPaging = usePagedRows(history, { id: 'logo-history' });

  return (
    <div className="min-h-screen bg-gradient-to-br from-navy-50 via-white to-teal-50 p-6">
      <div className="max-w-5xl mx-auto space-y-5">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-xl p-6 flex items-center space-x-4">
          <div className="w-14 h-14 bg-gradient-to-br from-navy-700 to-navy-900 rounded-2xl flex items-center justify-center">
            <ImageIcon className="w-7 h-7 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Branding</h1>
            <p className="text-gray-600">
              The hospital's name, address and logo, as they appear on printouts, the login
              screen, the Display Board and the app header.
            </p>
          </div>
        </div>

        {/* Clinic details card — the letterhead text */}
        <div className="bg-white rounded-2xl shadow-xl p-6">
          <h2 className="text-lg font-semibold text-gray-900">Clinic Details</h2>
          <p className="text-sm text-gray-500 mt-1 mb-4">
            Printed on the prescription letterhead and the receipt. A field left blank is left off
            the printout rather than printed empty.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {PROFILE_FIELDS.map((f) => (
              <div key={f.key} className={f.wide ? 'md:col-span-2' : ''}>
                <label className="block text-xs font-semibold text-gray-600 mb-1">
                  {f.label}
                  {f.required && <span className="text-red-500 ml-0.5">*</span>}
                </label>
                <input
                  type="text"
                  value={profile[f.key] || ''}
                  onChange={(e) => setProfile((prev) => ({ ...prev, [f.key]: e.target.value }))}
                  disabled={!canEdit || savingProfile || branding === null}
                  placeholder={f.placeholder}
                  dir={f.urdu ? 'rtl' : 'ltr'}
                  className={`w-full p-2.5 border-2 border-gray-200 rounded-lg disabled:opacity-50 ${
                    f.urdu ? 'urdu-text text-right' : ''
                  }`}
                />
              </div>
            ))}
          </div>
          {canEdit && (
            <div className="flex items-center justify-end gap-2 mt-4">
              {profileDirty && (
                <button
                  onClick={() => setProfile(savedProfile)}
                  disabled={savingProfile}
                  className="px-4 py-2 rounded-lg text-sm font-medium text-gray-500 hover:bg-gray-100 disabled:opacity-50"
                >
                  Discard
                </button>
              )}
              <button
                onClick={handleSaveProfile}
                disabled={!profileDirty || savingProfile}
                className="inline-flex items-center gap-1.5 px-5 py-2 rounded-lg text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save className="w-4 h-4" />
                {savingProfile ? 'Saving…' : 'Save Details'}
              </button>
            </div>
          )}
        </div>

        {/* Logo card */}
        <div className="bg-white rounded-2xl shadow-xl p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Clinic Logo</h2>
          <div className="flex flex-wrap items-center gap-8">
            {/* Current logo preview — light and dark surfaces so the admin sees both contexts */}
            <div className="flex items-center gap-4">
              <div className="w-32 h-32 rounded-2xl border border-gray-200 bg-white flex items-center justify-center p-4">
                {branding === null ? (
                  <RefreshCw className="w-6 h-6 text-gray-300 animate-spin" />
                ) : branding.hasCustomLogo ? (
                  <img
                    src={brandingAPI.logoUrl(branding.logoVersion)}
                    alt="Clinic logo"
                    className="max-w-full max-h-full object-contain"
                  />
                ) : (
                  <ClinicLogo size={88} variant="color" />
                )}
              </div>
              <div className="w-32 h-32 rounded-2xl bg-gray-900 flex items-center justify-center p-4">
                {branding === null ? null : branding.hasCustomLogo ? (
                  <img
                    src={brandingAPI.logoUrl(branding.logoVersion)}
                    alt="Clinic logo on dark"
                    className="max-w-full max-h-full object-contain"
                  />
                ) : (
                  <ClinicLogo size={88} variant="white" />
                )}
              </div>
            </div>

            <div className="flex-1 min-w-[260px]">
              <p className="text-sm text-gray-600 mb-1">
                {branding?.hasCustomLogo
                  ? 'A custom logo is active.'
                  : 'The built-in PAMH logo is active.'}
              </p>
              <p className="text-xs text-gray-400 mb-4">
                PNG, JPEG or WebP · max 2MB · a square image with a transparent background
                looks best on both light and dark surfaces.
              </p>
              {canEdit && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => fileRef.current?.click()}
                    disabled={working}
                    className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-medium text-sm disabled:opacity-50"
                  >
                    {working ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                    <span>{branding?.hasCustomLogo ? 'Replace Logo' : 'Upload Logo'}</span>
                  </button>
                  {branding?.hasCustomLogo && (
                    <button
                      onClick={handleRemove}
                      disabled={working}
                      className="flex items-center gap-2 px-4 py-2 text-red-600 border border-red-200 rounded-lg hover:bg-red-50 font-medium text-sm disabled:opacity-50"
                    >
                      <Trash2 className="w-4 h-4" />
                      <span>Remove</span>
                    </button>
                  )}
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    onChange={handleUpload}
                    className="hidden"
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Logo history — append-only: every upload is kept and can be brought back */}
        <div className="bg-white rounded-2xl shadow-xl p-6">
          <div className="flex items-center gap-2 mb-1">
            <History className="w-5 h-5 text-emerald-600" />
            <h2 className="text-lg font-semibold text-gray-900">Logo History</h2>
          </div>
          <p className="text-sm text-gray-500 mb-4">
            Every uploaded logo is kept here. You can switch back to any previous design at any
            time; nothing is ever deleted.
          </p>

          {history === null ? (
            <div className="flex items-center gap-2 text-sm text-gray-400 py-4">
              <RefreshCw className="w-4 h-4 animate-spin" /> Loading history…
            </div>
          ) : history.length === 0 ? (
            <p className="text-sm text-gray-400 py-2">No logos uploaded yet.</p>
          ) : (
            <div className="border border-gray-200 rounded-xl">
              <TablePager paging={historyPaging} noun="logo" className="px-4 !rounded-t-xl" />
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    <th className="px-4 py-2.5">Logo</th>
                    <th className="px-4 py-2.5">File</th>
                    <th className="px-4 py-2.5">Uploaded By</th>
                    <th className="px-4 py-2.5">Uploaded On</th>
                    <th className="px-4 py-2.5">Status</th>
                    {canEdit && <th className="px-4 py-2.5 text-right">Action</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {historyPaging.pageRows.map((row) => (
                    <tr key={row.id} className={row.active ? 'bg-emerald-50/50' : 'hover:bg-gray-50/60'}>
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-2">
                          <div className="w-12 h-12 rounded-lg border border-gray-200 bg-white flex items-center justify-center p-1.5">
                            <img
                              src={authedMediaUrl(brandingAPI.historyImageUrl(row.id))}
                              alt="Logo version"
                              className="max-w-full max-h-full object-contain"
                            />
                          </div>
                          <div className="w-12 h-12 rounded-lg bg-gray-900 flex items-center justify-center p-1.5">
                            <img
                              src={authedMediaUrl(brandingAPI.historyImageUrl(row.id))}
                              alt="Logo version on dark"
                              className="max-w-full max-h-full object-contain"
                            />
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-2.5 text-gray-700 max-w-[200px] truncate" title={row.originalFileName || ''}>
                        {row.originalFileName || '—'}
                      </td>
                      <td className="px-4 py-2.5 text-gray-700">{row.uploadedBy || '—'}</td>
                      <td className="px-4 py-2.5 text-gray-500 whitespace-nowrap">
                        {row.uploadedAt ? formatClinicDateTime(row.uploadedAt) : '—'}
                      </td>
                      <td className="px-4 py-2.5">
                        {row.active ? (
                          <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
                            Active
                          </span>
                        ) : (
                          <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">
                            Previous
                          </span>
                        )}
                      </td>
                      {canEdit && (
                        <td className="px-4 py-2.5 text-right">
                          {!row.active && (
                            <button
                              onClick={() => handleActivate(row)}
                              disabled={working}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-emerald-700 border border-emerald-200 hover:bg-emerald-50 disabled:opacity-50"
                            >
                              <RotateCcw className="w-3.5 h-3.5" />
                              <span>Use This Logo</span>
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
              <TablePager paging={historyPaging} noun="logo" position="bottom" className="px-4 !rounded-b-xl" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BrandingSettings;
